import { gql, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

interface UseUpdatedTimestamp {
  returnEmptyString?: boolean;
}

export const useUpdatedTimestamp = (props?: UseUpdatedTimestamp) => {
  const client = useApolloClient();
  return useCallback(
    /**
     * A function to retrieve the updated timestamp for an item in the apollo cache.
     * @param typename The typename for GQL - case sensitive
     * @param idFields An object containing the fields that uniquely identify the type. Typically this will be an object with an 'id' property.
     * This parameter's type only works with types and not interfaces due to a typescript quirk - spread your object if needed to avoid type errors.
     * @returns The updated timestamp for the object
     * @throws An error if the ID is not identifiable or the item/timestamp is not in the cache
     */
    (typename: string, idFields: Record<string, unknown>): string => {
      const id = client.cache.identify({ __typename: typename, ...idFields });
      if (!id) throw new Error('Id not identifiable');
      const cachedItem = client.readFragment({
        id: client.cache.identify({ __typename: typename, ...idFields }),
        fragment: gql`
          fragment UpdatedTimestampFragment on ${typename} {
            updatedTimestamp
          }
        `,
      });
      if (!cachedItem?.updatedTimestamp) {
        if (props?.returnEmptyString) return '';
        else throw new Error('Item not found in cache');
      }
      return cachedItem.updatedTimestamp;
    },
    [client],
  );
};
